import { useMemo } from "react";
import { XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, CartesianGrid, ComposedChart, Line, ReferenceLine, Legend } from "recharts";
import { formatINR } from "@/lib/format";
import { CHART_MARGIN, AXIS_LINE, TOOLTIP_STYLE, BAR_CURSOR, LEGEND_STYLE, SAVINGS_DOT, inrTick, inrValue } from "./chartTheme";

export function MonthlyCharts({ cashflow }) {
  const avgSpend = useMemo(() => {
    const monthsWithSpend = cashflow.filter((m) => m.expense > 0);
    return monthsWithSpend.length
      ? Math.round(monthsWithSpend.reduce((s, m) => s + m.expense, 0) / monthsWithSpend.length)
      : 0;
  }, [cashflow]);
  const avgSpendLabel = useMemo(
    () => ({ value: `Avg ${formatINR(avgSpend, { compact: true })}`, position: "insideTopRight", fill: "#A67C00", fontSize: 10 }),
    [avgSpend],
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="border border-[#D8D4C7] bg-white p-6">
        <div className="mb-4">
          <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#6C7565]">Twelve months · expenses only</div>
          <h4 className="font-serif text-2xl tracking-tight">Monthly spending</h4>
        </div>
        <div className="h-72" data-testid="chart-monthly-spending">
          <ResponsiveContainer>
            <BarChart data={cashflow} margin={CHART_MARGIN}>
              <CartesianGrid stroke="#EAE7DD" vertical={false} />
              <XAxis dataKey="month" stroke="#6C7565" fontSize={10} tickLine={false} axisLine={AXIS_LINE} />
              <YAxis stroke="#6C7565" fontSize={10} tickLine={false} axisLine={AXIS_LINE} tickFormatter={inrTick} />
              <Tooltip contentStyle={TOOLTIP_STYLE} formatter={inrValue} cursor={BAR_CURSOR} />
              <ReferenceLine y={avgSpend} stroke="#A67C00" strokeDasharray="4 4" label={avgSpendLabel} />
              <Bar dataKey="expense" name="Spending" fill="#D94F3D" maxBarSize={28} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="border border-[#D8D4C7] bg-white p-6">
        <div className="mb-4">
          <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#6C7565]">Side by side · with savings line</div>
          <h4 className="font-serif text-2xl tracking-tight">Income vs Expenses</h4>
        </div>
        <div className="h-72" data-testid="chart-income-vs-expense">
          <ResponsiveContainer>
            <ComposedChart data={cashflow} margin={CHART_MARGIN}>
              <CartesianGrid stroke="#EAE7DD" vertical={false} />
              <XAxis dataKey="month" stroke="#6C7565" fontSize={10} tickLine={false} axisLine={AXIS_LINE} />
              <YAxis stroke="#6C7565" fontSize={10} tickLine={false} axisLine={AXIS_LINE} tickFormatter={inrTick} />
              <Tooltip contentStyle={TOOLTIP_STYLE} formatter={inrValue} cursor={BAR_CURSOR} />
              <Legend wrapperStyle={LEGEND_STYLE} />
              <Bar dataKey="income" name="Income" fill="#2E6F40" maxBarSize={16} />
              <Bar dataKey="expense" name="Expense" fill="#D94F3D" maxBarSize={16} />
              <Line type="monotone" dataKey="savings" name="Savings" stroke="#1A1F16" strokeWidth={2} dot={SAVINGS_DOT} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
